import { useCallback, useMemo, useRef } from 'react';
import type SwiperCore from 'swiper';
import type { NavigationOptions } from 'swiper/types';

export type SwiperNavigationConfig = {
  navigation: NavigationOptions;
  onBeforeInit: (swiper: SwiperCore) => void;
};

export default function useSwiperNavigation<
  TElement extends HTMLElement = HTMLDivElement,
>() {
  const prevElRef = useRef<TElement>(null);
  const nextElRef = useRef<TElement>(null);
  const navigation = useMemo(
    () => ({ prevEl: null, nextEl: null }),
    [],
  ) satisfies NavigationOptions;

  const onBeforeInit = useCallback((swiper: SwiperCore) => {
    const prevEl = prevElRef.current;
    const nextEl = nextElRef.current;

    if (!prevEl || !nextEl) {
      throw new Error(
        'Swiper navigation elements must be mounted before initialization',
      );
    }

    const paramsNavigation = swiper.params.navigation;
    const originalNavigation = swiper.originalParams.navigation;

    if (
      !paramsNavigation ||
      typeof paramsNavigation !== 'object' ||
      !originalNavigation ||
      typeof originalNavigation !== 'object'
    ) {
      throw new Error('Swiper navigation must be configured as an object');
    }

    Object.assign(paramsNavigation, { prevEl, nextEl });
    Object.assign(originalNavigation, { prevEl, nextEl });
  }, []);

  return {
    navigation,
    nextElRef,
    onBeforeInit,
    prevElRef,
  };
}
